import { useEffect, useRef, useState } from "react";
import Lenis from "@studio-freight/lenis";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBookOpen,
  FiCheckCircle,
  FiCreditCard,
  FiStar,
} from "react-icons/fi";

const BLOB_BASE_URL =
  "https://hlineweb-f6dwh8btedejg7fz.z03.azurefd.net//hlinefiles/HLineWeb/Projects/optimized_blender_imgs/";

function GlassShards({ count = 15 }) {
  const shards = Array.from({ length: count });

  return (
    <>
      {shards.map((_, i) => {
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const size = 40 + Math.random() * 60;
        const delay = Math.random() * 6;
        const duration = 10 + Math.random() * 10;

        return (
          <div
            key={i}
            className="shard"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: `${size}px`,
              height: `${size}px`,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
            }}
          />
        );
      })}
    </>
  );
}

export default function Process({
  frameCount = 133,
  getFrameSrc = (i) => `${BLOB_BASE_URL}${i + 1}.jpg`,
}) {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const imagesRef = useRef([]);
  const loadedFlagsRef = useRef([]);
  const current = useRef({ frame: 0, targetFrame: 0 });
  const [activeIndex, setActiveIndex] = useState(0);

  const captionRanges = [
    {
      start: 1,
      end: 12,
      title: "01",
      text: `Ingot Loading: Aluminium ingots are carefully loaded into the melting furnace. 
    This stage ensures that raw materials are evenly distributed and ready for uniform heating, 
    preventing impurities and improving the overall quality of the melt.`,
    },
    {
      start: 13,
      end: 21,
      title: "02",
      text: `Melting in Furnace: The ingots are melted at extremely high temperatures 
    to form molten aluminium. Proper heat control is crucial here to ensure 
    a smooth, consistent melt and maintain alloy integrity.`,
    },
    {
      start: 22,
      end: 27,
      title: "03",
      text: `Billet Casting: The molten aluminium is cast into solid cylindrical billets 
    through a controlled cooling process. This step determines the grain structure 
    and surface quality, ensuring strong and defect-free billets.`,
    },
    {
      start: 28,
      end: 59,
      title: "04",
      text: `Heating & Burning: Billets are re-heated to remove surface oxides and 
    improve ductility before extrusion. Controlled temperature gradients prevent 
    cracking and enhance press performance.`,
    },
    {
      start: 60,
      end: 78,
      title: "05",
      text: `Extrusion Press: The heated billets are pressed through dies to form 
    various aluminium profiles. This process defines the cross-section and precision 
    of the final product.`,
    },
    {
      start: 79,
      end: 97,
      title: "06",
      text: `Cutting: The extruded profiles are cut into required lengths with precision saws. 
    Accurate cutting ensures seamless fitting in subsequent assembly stages.`,
    },
    {
      start: 98,
      end: 117,
      title: "07",
      text: `Fitting: The cut profiles are assembled into frames or structural sections. 
    Attention to alignment and tolerance ensures consistent performance and aesthetics.`,
    },
    {
      start: 118,
      end: 134,
      title: "08",
      text: `Window Frame Assembly: Final assembly brings together all components into 
    durable, corrosion-resistant frames. Each part is inspected for quality and finish 
    before packaging and dispatch.`,
    },
  ];

  const pickFrame = (idx) => {
    const flags = loadedFlagsRef.current;
    if (flags[idx]) return idx;
    for (let j = idx; j >= 0; j--) if (flags[j]) return j;
    for (let j = idx; j < flags.length; j++) if (flags[j]) return j;
    return 0;
  };

  const draw = () => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    current.current.frame +=
      (current.current.targetFrame - current.current.frame) * 0.1;

    const idx = pickFrame(Math.round(current.current.frame));
    const img = imagesRef.current[idx];
    if (!img?.naturalWidth) return;

    const vw = canvas.width;
    const vh = canvas.height;
    const scale = Math.max(vw / img.naturalWidth, vh / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    const dx = (vw - dw) / 2;
    const dy = (vh - dh) / 2;

    ctx.clearRect(0, 0, vw, vh);
    ctx.drawImage(img, dx, dy, dw, dh);

    const frame = Math.round(current.current.frame) + 1;
    let currentIndex = captionRanges.findIndex(
      (c) => frame >= c.start && frame <= c.end
    );

    if (currentIndex === -1) {
      if (frame <= captionRanges[0].start + 1) currentIndex = 0;
      else if (frame >= captionRanges[captionRanges.length - 1].end - 1)
        currentIndex = captionRanges.length - 1;
    }

    if (currentIndex !== -1 && currentIndex !== activeIndex) {
      setActiveIndex(currentIndex);
    }

    requestAnimationFrame(draw);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    ctxRef.current = canvas.getContext("2d");
    canvas.width = 400;
    canvas.height = 400;

    imagesRef.current = new Array(frameCount);
    loadedFlagsRef.current = new Array(frameCount).fill(false);

    for (let i = 0; i < frameCount; i++) {
      const img = new Image();
      img.onload = () => {
        loadedFlagsRef.current[i] = true;
        if (i === 0) {
          current.current.frame = 0;
          setActiveIndex(0);
          draw();
        }
      };
      img.src = getFrameSrc(i);
      imagesRef.current[i] = img;
    }

    const lenis = new Lenis({ smooth: true, lerp: 0.1 });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    lenis.on("scroll", ({ scroll }) => {
      if (!sectionRef.current) return;

      const sectionTop = sectionRef.current.offsetTop;
      const sectionBottom = sectionTop + sectionRef.current.offsetHeight;
      const sectionHeight =
        sectionRef.current.offsetHeight - window.innerHeight;
      const isInside = scroll >= sectionTop && scroll <= sectionBottom;

      const rawProgress = (scroll - sectionTop) / sectionHeight;
      const progress = Math.min(Math.max(rawProgress, 0), 1);

      current.current.targetFrame = progress * (frameCount - 1);

      if (scroll <= sectionTop + 5) {
        setActiveIndex(0);
      }

      if (scroll >= sectionBottom - 5) {
        setActiveIndex(captionRanges.length - 1);
      }
    });

    return () => lenis.destroy();
  }, [frameCount]);

  return (
    <div className="container">
      <div
        ref={sectionRef}
        id="process"
        className="relative h-[500vh] overflow-visible"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <GlassShards count={25} />
        </div>
        <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-white/70">
          Our Process
        </p>
        <h2 className="mt-3 text-2xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight">
          From raw aluminium to architectural perfection.
        </h2>
        <p className="mt-4 text-white/80 md:text-lg leading-relaxed max-w-3xl">
          Every product begins with precision and ends with performance —
          engineered for durability, aesthetics, and excellence in every frame.
        </p>

        <div className="sticky top-0 h-screen w-full flex items-center justify-center">
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="order-1 w-full lg:w-1/3 flex justify-center items-center">
              <div className="relative w-[80vw] sm:w-[400px] md:w-[450px] lg:w-[500px] aspect-square max-w-full">
                <div className="absolute inset-0 border-4 border-dashed border-gray-600"></div>
                <canvas
                  ref={canvasRef}
                  className="w-full h-full rounded-xl shadow-lg object-contain"
                />
              </div>
            </div>

            <div className="hidden order-2  lg:flex relative flex-col items-center justify-center mx-6 md:mx-8 lg:mx-10">
              <div className="absolute w-[1px] h-full bg-gradient-to-b from-white/10 via-gray-600/40 to-transparent" />
              {captionRanges.map((step, i) => {
                const showTitle = i === activeIndex || i === activeIndex + 1;
                if (!showTitle) return null;

                const isActive = i === activeIndex;
                return (
                  <div
                    key={i}
                    className="relative flex flex-col items-center transition-all duration-700"
                    style={{ marginTop: i === activeIndex ? "0px" : "120px" }}
                  >
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
                        isActive
                          ? "bg-white text-black shadow-lg scale-110"
                          : "bg-gray-600 text-white/80 scale-90"
                      }`}
                    >
                      {step.title}
                    </div>
                    {i !== captionRanges.length - 1 && (
                      <div className="w-[1px] h-20 sm:h-24 bg-gradient-to-b from-gray-400/70 to-transparent" />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="order-3 w-full lg:w-1/3 text-center lg:text-left text-white max-w-xl leading-relaxed">
              <AnimatePresence mode="wait">
                <motion.div
                  key={captionRanges[activeIndex].title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3 text-white flex flex-row items-center justify-center lg:justify-start gap-2">
                    <span className="bg-white text-black rounded-full w-12 h-12 flex items-center justify-center font-bold lg:hidden">
                      {captionRanges[activeIndex].title}
                    </span>
                    {captionRanges[activeIndex].text.split(":")[0]}
                  </h3>
                  <p className="text-gray-300 text-base sm:text-lg lg:text-[18px] whitespace-pre-line">
                    {captionRanges[activeIndex].text.split(":")[1]}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <div className="text-white pb-14">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-10">
          Our 4-Step Manufacturing & Installation Process
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 px-4">
          {[
            {
              id: 1,
              icon: <FiBookOpen className="text-4xl" />,
              title: "1. Extrusion & Surface Finishing",
              desc: "High-quality aluminium billets are extruded into precise profiles, then powder-coated or anodized for strength, durability, and corrosion resistance.",
            },
            {
              id: 2,
              icon: <FiStar className="text-4xl" />,
              title: "2. Fabrication & Cutting",
              desc: "Profiles are cut to exact frame dimensions and machined for hardware integration — ensuring perfect fit and finish for every door and window system.",
            },
            {
              id: 3,
              icon: <FiCreditCard className="text-4xl" />,
              title: "3. Assembly & Fitting",
              desc: "Frames and shutters are assembled with gaskets, rollers, hinges, and locks — delivering smooth operation and precise alignment.",
            },
            {
              id: 4,
              icon: <FiCheckCircle className="text-4xl" />,
              title: "4. Site Installation & Delivery",
              desc: "Fully finished systems are packed, transported, and installed on-site — ensuring quality fitting, sealing, and final inspection before handover.",
            },
          ].map((step) => (
            <div
              key={step.id}
              className="flex flex-col items-center justify-center text-center 
                   transition-transform hover:scale-105"
            >
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full 
                        border-2 border-white mb-5"
              >
                {step.icon}
              </div>
              <h3 className="font-semibold text-lg md:text-xl mb-1">
                {step.title}
              </h3>
              <p className="text-white/80 text-sm md:text-base max-w-xs">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
